const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);
const REQUESTED_WITH_HEADER = "X-Requested-With";
const REQUESTED_WITH_VALUE = "XMLHttpRequest";
const CSRF_HEADER = "X-CSRF-Token";
const CSRF_COOKIE = "csrf_token_js";

let installed = false;

function isSameOrigin(input: RequestInfo | URL): boolean {
  try {
    const base = window.location.href;
    let urlString: string;
    if (typeof input === "string") {
      urlString = input;
    } else if (input instanceof URL) {
      urlString = input.href;
    } else if (typeof Request !== "undefined" && input instanceof Request) {
      urlString = input.url;
    } else {
      return false;
    }
    const resolved = new URL(urlString, base);
    return resolved.origin === window.location.origin;
  } catch {
    return false;
  }
}

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const raw = document.cookie;
  if (!raw) return undefined;
  for (const part of raw.split(";")) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf("=");
    if (eq < 0) continue;
    if (trimmed.slice(0, eq) === name) {
      try {
        return decodeURIComponent(trimmed.slice(eq + 1));
      } catch {
        return trimmed.slice(eq + 1);
      }
    }
  }
  return undefined;
}

// Task #747: when the user lands on a page whose first network action is
// an unsafe POST (e.g. /magic-link/verify, /verify-email, or a direct
// /login submission before AuthProvider's session-check GET has settled),
// no CSRF cookie has been minted yet. We lazily bootstrap one by hitting
// the auth session endpoint, which runs through the synchronizer-token
// middleware unconditionally and issues both `csrf_token` (HttpOnly) and
// the JS-readable `csrf_token_js` twin cookie. The promise is memoised
// so concurrent unsafe requests share a single round-trip.
let bootstrapPromise: Promise<void> | null = null;
function bootstrapCsrfCookie(originalFetch: typeof window.fetch): Promise<void> {
  if (bootstrapPromise) return bootstrapPromise;
  // Compute the API base relative to this app's BASE_URL so the request
  // routes through the artifact's path prefix (and doesn't escape to /).
  const base = (() => {
    try {
      const v = (import.meta as unknown as { env?: { BASE_URL?: string } }).env?.BASE_URL;
      return typeof v === "string" && v.length > 0 ? v : "/";
    } catch {
      return "/";
    }
  })();
  const url = `${base}api/v1/auth/session`;
  bootstrapPromise = originalFetch(url, {
    method: "GET",
    credentials: "include",
    headers: { [REQUESTED_WITH_HEADER]: REQUESTED_WITH_VALUE },
  })
    .then(() => undefined)
    .catch(() => undefined);
  return bootstrapPromise;
}

export function installFetchCsrfHeader(): void {
  if (installed) return;
  if (typeof window === "undefined" || typeof window.fetch !== "function") return;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
    let method = "GET";
    if (init?.method) {
      method = init.method.toUpperCase();
    } else if (typeof Request !== "undefined" && input instanceof Request) {
      method = input.method.toUpperCase();
    }

    if (SAFE_METHODS.has(method)) {
      return originalFetch(input, init);
    }

    if (!isSameOrigin(input)) {
      return originalFetch(input, init);
    }

    // Lazily mint a CSRF cookie if this is the first unsafe request and
    // nothing has bootstrapped one yet. Safe to await even when the
    // cookie already exists in the common case (we early-out below).
    if (!readCookie(CSRF_COOKIE)) {
      await bootstrapCsrfCookie(originalFetch);
    }

    const headers = new Headers(
      init?.headers ?? (typeof Request !== "undefined" && input instanceof Request ? input.headers : undefined),
    );
    if (!headers.has(REQUESTED_WITH_HEADER)) {
      headers.set(REQUESTED_WITH_HEADER, REQUESTED_WITH_VALUE);
    }
    if (!headers.has(CSRF_HEADER)) {
      const token = readCookie(CSRF_COOKIE);
      if (token) headers.set(CSRF_HEADER, token);
    }

    const nextInit: RequestInit = { ...(init ?? {}), headers };
    return originalFetch(input, nextInit);
  };

  installed = true;
}
